import React, { useEffect, useMemo, useState, useCallback } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import ActionDropdownItem from "../../components/Common/PageTitle/ActionDropdownItem";
import { Link } from "react-router-dom";
import { useBanking } from "../../DataStore/BankingContext";
import { useCurrency } from "../../util/SystemVariables";
import { formatDate, numberFormat } from "../../util/araknerdlib";
import { SkeletonBar } from "../../util/skeleton/Skeleton";
import Pagination from "../../components/Common/Pagination";
import FilterBar from "../../components/Common/FilterBar"; // ✅ use the global FilterBar

/* ----------------- tiny helpers ----------------- */
const badgeTone = (s) => (s === "Reconciled" ? "success" : s === "Pending" ? "warning" : "secondary");
const directionTone = (d) => (d === "Incoming" ? "success" : d === "Outgoing" ? "danger" : "secondary");

const ellipsisStyle = (maxWidth) => ({
  maxWidth,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

/* ----------------- row components ----------------- */
const TxRow = ({ tx, currency }) => {
  const amountText = `${currency} ${numberFormat(tx.amount)}`;

  return (
    <tr>
      <td style={ellipsisStyle(110)} title={formatDate(tx.cheque_date)}>
        {formatDate(tx.cheque_date)}
      </td>

      <td style={ellipsisStyle(160)}>
        <span className="d-inline-flex align-items-center">
          <span
            className={`badge badge-light text-${directionTone(tx.direction)} mr-2`}
            style={{
              minWidth: 26,
              height: 26,
              borderRadius: "999px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.05)",
            }}
            title={tx.direction}
          >
            <i className={tx.direction === "Incoming" ? "fa fa-arrow-down" : "fa fa-arrow-up"} />
          </span>
          <span style={ellipsisStyle(110)} title={tx.payment_type}>
            {tx.payment_type}
          </span>
        </span>
      </td>

      <td className="text-muted" style={ellipsisStyle(120)} title={tx.reference}>
        {tx.reference}
      </td>

      <td style={ellipsisStyle(180)} title={tx.cash_account?.account_name || ""}>
        {tx.cash_account?.account_name}
      </td>

      <td style={ellipsisStyle(180)} title={tx.ledger_account?.account_name || ""}>
        {tx.ledger_account?.account_name}
      </td>

      <td className="text-muted" style={ellipsisStyle(260)} title={tx.narration || ""}>
        {tx.narration || <span className="text-muted">—</span>}
      </td>

      <td className="text-right font-weight-semibold" style={ellipsisStyle(140)} title={amountText}>
        {amountText}
      </td>

      <td className="text-center" style={ellipsisStyle(120)}>
        <span className={`badge badge-${badgeTone(tx.reconcile_status)}`}>{tx.reconcile_status}</span>
      </td>
    </tr>
  );
};

/* ----------------- skeleton loader ----------------- */
const TxSkeletonRow = () => (
  <tr>
    <td><SkeletonBar w="70%" h={10} /></td>
    <td>
      <div className="d-flex align-items-center">
        <SkeletonBar w={26} h={26} style={{ borderRadius: "999px", marginRight: 8 }} />
        <SkeletonBar w="60%" h={10} />
      </div>
    </td>
    <td><SkeletonBar w="60%" h={10} /></td>
    <td><SkeletonBar w="80%" h={10} /></td>
    <td><SkeletonBar w="80%" h={10} /></td>
    <td><SkeletonBar w="90%" h={10} /></td>
    <td className="text-right">
      <SkeletonBar w="60%" h={10} style={{ display: "block", marginLeft: "auto" }} />
    </td>
    <td className="text-center">
      <SkeletonBar w="70%" h={18} style={{ borderRadius: "999px" }} />
    </td>
  </tr>
);

const TxSkeletonBody = ({ rows = 4 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <TxSkeletonRow key={i} />
    ))}
  </>
);

/* ----------------- main page ----------------- */
const CheckingTransactionsPage = () => {
  const {
    getCheckingTransactions,
    setMounted,
    transactions,
    metaData,
    setCurrentPage,
    filters,
    setFilters,
  } = useBanking();

  const currency = useCurrency();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setMounted?.(true);
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTransactions = useCallback(async (payload = null) => {
    try {
      setLoading(true);
      if (typeof getCheckingTransactions === "function") {
        // ✅ if your context supports filters as a param, pass them
        // If not supported, it will just ignore extra args safely in JS
        await getCheckingTransactions(payload);
      }
    } finally {
      setLoading(false);
    }
  }, [getCheckingTransactions]);

  const applyFilters = async () => {
    const payload = Object.entries(filters || {}).reduce((acc, [k, v]) => {
      if (v !== "" && v != null) acc[k] = v;
      return acc;
    }, {});
    await loadTransactions(payload);
  };

  const clearFilters = async () => {
    setFilters({
      search: "",
      direction: "",
      payment_type: "",
      reconcile_status: "",
      date_from: "",
      date_to: "",
    });
    await loadTransactions({});
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...(prev || {}), [name]: value }));
  };

  const rows = useMemo(() => (Array.isArray(transactions) ? transactions : []), [transactions]);
  const hasRows = rows.length > 0;

  const PageActions = () => (
    <>
      <ActionDropdownItem
        iconClass={`fa fa-sync${loading ? " fa-spin" : ""}`}
        label={loading ? "Refreshing…" : "Refresh"}
        onClick={() => loadTransactions(
          Object.entries(filters || {}).reduce((acc,[k,v]) => (v !== "" && v != null ? (acc[k]=v, acc) : acc), {})
        )}
      />
    </>
  );

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Checking Transactions"
          description="Manage your checking account transactions and reconciliations."
          iconClass="fa fa-credit-card"
          PageActions={PageActions}
          sticky={true}
          Pagination={
            metaData ? (
              <Pagination metaData={metaData} onPageChange={(page) => setCurrentPage(page)} />
            ) : null
          }
        />

        {/* ✅ Global FilterBar */}
        <FilterBar
          title="Transaction listing"
          subtitle="Filter transactions by various criteria"
          controls={[
            {
              type: "select",
              value: String(filters?.direction || ""),
              placeholder: "All directions",
              width: 170,
              options: [
                { value: "", label: "All directions" },
                { value: "Incoming", label: "Incoming" },
                { value: "Outgoing", label: "Outgoing" },
              ],
              onChange: (e) => handleFilterChange("direction", e.target.value),
            },
            {
              type: "select",
              value: String(filters?.payment_type || ""),
              placeholder: "All payment types",
              width: 190,
              options: [
                { value: "", label: "All payment types" },
                { value: "Cheque", label: "Cheque" },
                { value: "EFT", label: "EFT" },
                { value: "RTGS", label: "RTGS" },
                { value: "Wire", label: "Wire" },
                { value: "MobileMoney", label: "Mobile Money" },
                { value: "Other", label: "Other" },
              ],
              onChange: (e) => handleFilterChange("payment_type", e.target.value),
            },
            {
              type: "select",
              value: String(filters?.reconcile_status || ""),
              placeholder: "Select status",
              width: 170,
              options: [
                { value: "", label: "All statuses" },
                { value: "Pending", label: "Pending" },
                { value: "Reconciled", label: "Reconciled" },
              ],
              onChange: (e) => handleFilterChange("reconcile_status", e.target.value),
            },
            {
              type: "dateRange",
              fromValue: filters?.date_from,
              toValue: filters?.date_to,
              onChangeFrom: (e) =>handleFilterChange("date_from", e.target.value),
              onChangeTo: (e) => handleFilterChange("date_to", e.target.value),
            },
            
          ]}
          actions={[
            { label: loading ? "Applying…" : "Apply", iconClass: "fa fa-filter", onClick: applyFilters, variant: "primary" },
          ]}
        />

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {/* Table */}
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: 110 }}>Date</th>
                    <th style={{ minWidth: 160 }}>P. Type / Direction</th>
                    <th style={{ minWidth: 120 }}>Reference</th>
                    <th style={{ minWidth: 180 }}>Origin Account</th>
                    <th style={{ minWidth: 180 }}>Dest. Account</th>
                    <th style={{ minWidth: 260 }}>Description</th>
                    <th className="text-right" style={{ minWidth: 140 }}>Amount</th>
                    <th className="text-center" style={{ minWidth: 120 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && !hasRows && <TxSkeletonBody rows={4} />}

                  {!loading && hasRows && rows.map((tx) => (
                    <TxRow key={tx.id} tx={tx} currency={currency} />
                  ))}

                  {!loading && !hasRows && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        <i className="fa fa-inbox mb-2 d-block" />
                        No checking transactions match the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div className="small text-muted">
                Need to add or adjust a transaction? <Link to="/checking/new">Record a new entry</Link>
              </div>
             
            </div>
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default CheckingTransactionsPage;
